import { Box, Chip, Skeleton, Stack, Tooltip } from '@mui/material';
import React, { useMemo } from 'react';
import ElementDataRow from 'components/common/element-data/ElementDataRow';
import { ElementMetadataFieldName, IElementDataTags } from 'components/common/element-data/types';
import useBundleTranslation from 'i18n';
import { useQuery } from '@tanstack/react-query';
import { APIResponse } from 'tools/types';
import { editorTopicsAPI, TopicsInfo } from 'api/editor/topics';
import ElementDataRowLabel from 'components/common/element-data/ElementDataRowLabel';
import ElementDataRowValue from 'components/common/element-data/ElementDataRowValue';
import { dispatchCustomEvent } from 'tools/events';
import { openInNewTab } from 'tools/tools';
import { adjustTopicsDataFormat, getFieldElementMetadataSettings } from 'components/common/element-data/functions';

export default function Tags(props: IElementDataTags) {
    const { t } = useBundleTranslation(['components/common/element_data']);
    const { elementId = 0, topicsNeedToLoad = false, topicIdsToLoad = [], topics: topicsData = {}, settings } = props;
    const fieldKey: ElementMetadataFieldName = 'tags';
    const { fieldMetadataDisplay, fieldMetadataOrder } = getFieldElementMetadataSettings(settings, fieldKey);

    const { data: loadedTopics, isLoading } = useQuery<APIResponse<TopicsInfo>, Error>({
        queryKey: [`tile-${elementId}-topics`],
        queryFn: () => {
            return editorTopicsAPI.getTopicsInfo(topicIdsToLoad);
        },
        enabled: fieldMetadataDisplay && topicsNeedToLoad && topicIdsToLoad.length > 0,
    });

    const topics: any[] = useMemo((): any => {
        if (topicsNeedToLoad) {
            if (isLoading || !loadedTopics) return {};

            return loadedTopics?.data ? adjustTopicsDataFormat(loadedTopics.data) : {};
        }
        return adjustTopicsDataFormat(topicsData);
    }, [loadedTopics, isLoading, topicsData]);

    const tagsSectionKeys = Object.keys(topics).filter((key) => key == 'default-topic-type');

    const onTagClick = (tag: any, sectionName?: string) => {
        if (settings?.isAppPanel) {
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'tag-click',
                payload: {
                    item: tag,
                    sectionName,
                },
            });
        } else {
            openInNewTab(`/search/global?tag_name=${tag.name}`);
        }
    };

    if (!fieldMetadataDisplay) return null;

    if (isLoading) {
        return <Skeleton sx={{ width: '100%', height: '29px' }} />;
    }

    if (Object.keys(topics).length == 0 || tagsSectionKeys.length == 0) {
        return null;
    }

    return (
        <>
            {tagsSectionKeys.map((sectionKey: any) => {
                const section = topics[sectionKey];
                return (
                    <ElementDataRow order={fieldMetadataOrder} key={sectionKey}>
                        <ElementDataRowLabel icon={{ type: 'mi', value: 'tags' }}>
                            {settings?.viewSize == 'medium' ? t(`element_data_label.${fieldKey}`) : null}
                        </ElementDataRowLabel>
                        <ElementDataRowValue>
                            <Stack direction="row" flexWrap={'wrap'} sx={{ mt: '-5px', ml: -0.5 }}>
                                {section.topics.map((tag: any) => {
                                    const needToCutLabel = tag.name.length > 30;
                                    const label = needToCutLabel ? tag.name.substring(0, 30) + '...' : tag.name;
                                    return (
                                        <Box className={'tag-item'} sx={{ mt: 0.5, ml: 0.5 }} key={tag.topicId}>
                                            <Tooltip
                                                key={`info-tag-tooltip-${tag.topicId}`}
                                                title={needToCutLabel ? tag.name : ''}
                                            >
                                                <Chip
                                                    key={`info-tag-${tag.topicId}`}
                                                    onClick={() => onTagClick(tag, section.topicTypeName)}
                                                    label={label}
                                                    color="primary"
                                                />
                                            </Tooltip>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </ElementDataRowValue>
                    </ElementDataRow>
                );
            })}
        </>
    );
}
